import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Activity, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const SHIFTS = [
  { value: "day",   label: "Day" },
  { value: "swing", label: "Swing" },
  { value: "night", label: "Night" },
];
const EMPTY = { work_order_id: "", machine_id: "", operator_name: "", shift: "day", units_produced: "", scrap: "", cycle_seconds: "", notes: "" };

export default function GmesProduction() {
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/gmes/production-logs").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/gmes/machines").then(({ data }) => setMachines(data)).catch(() => {});
    api.get("/gmes/work-orders").then(({ data }) => setWorkOrders(data)).catch(() => {});
  }, []);

  const machineMap = useMemo(() => Object.fromEntries(machines.map((m) => [m.id, m.name])), [machines]);
  const woMap = useMemo(() => Object.fromEntries(workOrders.map((w) => [w.id, `${w.wo_number} · ${w.product}`])), [workOrders]);

  const totals = items.reduce((a, l) => ({
    units: a.units + (l.units_produced || 0),
    scrap: a.scrap + (l.scrap || 0),
  }), { units: 0, scrap: 0 });

  const save = async (e) => {
    e.preventDefault();
    if (!form.machine_id) { toast.error("Machine required"); return; }
    setSaving(true);
    try {
      await api.post("/gmes/production-logs", {
        ...form,
        units_produced: form.units_produced ? parseFloat(form.units_produced) : 0,
        scrap: form.scrap ? parseFloat(form.scrap) : 0,
        cycle_seconds: form.cycle_seconds ? parseFloat(form.cycle_seconds) : null,
      });
      toast.success("Production logged");
      setOpen(false); setForm(EMPTY); load();
      // reload WOs to reflect rolled-up progress
      api.get("/gmes/work-orders").then(({ data }) => setWorkOrders(data)).catch(() => {});
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this log entry? (Will subtract from WO totals.)")) return;
    await api.delete(`/gmes/production-logs/${id}`);
    load();
    api.get("/gmes/work-orders").then(({ data }) => setWorkOrders(data)).catch(() => {});
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
            <Link to="/app/shopfloor" className="hover:text-primary">Shop Floor</Link> / Production
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="gmes-production-title">
            Production Logs
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Log shift-by-shift output. Rolls up to work orders and OEE automatically.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="add-production-btn">
          <Plus className="w-4 h-4 mr-2" /> Log production
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="border border-border bg-card rounded-sm p-4"><div className="overline text-muted-foreground">Entries</div><div className="font-display font-black text-3xl mt-1 tabular-nums">{items.length}</div></div>
        <div className="border border-border bg-card rounded-sm p-4"><div className="overline text-muted-foreground">Units produced</div><div className="font-display font-black text-3xl mt-1 tabular-nums text-emerald-600">{totals.units}</div></div>
        <div className="border border-border bg-card rounded-sm p-4"><div className="overline text-muted-foreground">Scrap</div><div className="font-display font-black text-3xl mt-1 tabular-nums text-red-600">{totals.scrap}</div></div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center bg-card/40">
          <Activity className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display text-lg mt-4">No production yet</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Log your first shift output. Everything flows into the OEE dashboard.
          </p>
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50">
              <tr className="text-left">
                {["When", "Machine", "Work Order", "Operator", "Shift", "Units", "Scrap", "Cycle", ""].map((h) => (
                  <th key={h} className="px-3 py-2 font-mono-plex text-[10px] uppercase tracking-widest text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-t border-border" data-testid={`prod-row-${l.id}`}>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {l.logged_at ? formatDistanceToNow(new Date(l.logged_at), { addSuffix: true }) : "—"}
                  </td>
                  <td className="px-3 py-2 font-medium">{machineMap[l.machine_id] || "—"}</td>
                  <td className="px-3 py-2 text-xs">{woMap[l.work_order_id] || <span className="text-muted-foreground">Unlinked</span>}</td>
                  <td className="px-3 py-2 text-xs">{l.operator_name || "—"}</td>
                  <td className="px-3 py-2"><Badge className="rounded-sm bg-secondary text-foreground hover:bg-secondary font-mono-plex text-[10px] uppercase tracking-widest">{l.shift}</Badge></td>
                  <td className="px-3 py-2 tabular-nums text-emerald-600">{l.units_produced}</td>
                  <td className="px-3 py-2 tabular-nums text-red-600">{l.scrap || 0}</td>
                  <td className="px-3 py-2 tabular-nums text-xs">{l.cycle_seconds != null ? `${l.cycle_seconds}s` : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button title="Delete" onClick={() => remove(l.id)} className="p-1 text-muted-foreground hover:text-destructive hover:bg-red-50 rounded-sm">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-sm">
          <DialogHeader>
            <div className="overline text-accent">◉ LOG PRODUCTION</div>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">Shift output</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 pt-2">
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Machine *</label>
              <Select value={form.machine_id} onValueChange={(v) => setForm({ ...form, machine_id: v })}>
                <SelectTrigger className="rounded-sm" data-testid="prod-machine-select"><SelectValue placeholder="Choose machine" /></SelectTrigger>
                <SelectContent>{machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Work order (optional)</label>
              <Select value={form.work_order_id || "none"} onValueChange={(v) => setForm({ ...form, work_order_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Unlinked" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unlinked</SelectItem>
                  {workOrders.filter((w) => w.status !== "complete" && w.status !== "cancelled").map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.wo_number} · {w.product}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Operator</label>
                <Input value={form.operator_name} onChange={(e) => setForm({ ...form, operator_name: e.target.value })} className="rounded-sm" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Shift</label>
                <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{SHIFTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Units produced</label>
                <Input type="number" step="1" value={form.units_produced} onChange={(e) => setForm({ ...form, units_produced: e.target.value })} className="rounded-sm" data-testid="prod-units-input" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Scrap</label>
                <Input type="number" step="1" value={form.scrap} onChange={(e) => setForm({ ...form, scrap: e.target.value })} className="rounded-sm" />
              </div>
              <div>
                <label className="overline text-muted-foreground block mb-1.5">Cycle (sec)</label>
                <Input type="number" step="0.1" value={form.cycle_seconds} onChange={(e) => setForm({ ...form, cycle_seconds: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground block mb-1.5">Notes</label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm resize-none" />
            </div>
            <Button type="submit" disabled={saving} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="save-production-btn">
              {saving ? "Saving…" : "Log production"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
